/**
 * What the agent offers on each PMS tab, and readable names for tools.
 */

// For now the agent is for super admins only; the backend enforces the same on every
// /api/agent route (AGENT_ACCESS_ROLES in Dave-PMS/src/agent/types.ts). Nothing of the
// agent (tab, launcher, drawer) renders for other roles.
export const AGENT_ROLES = ['SUPERADMIN'];
export const AGENT_ADMIN_ROLES = ['SUPERADMIN', 'PROPERTYADMIN'];

export const canUseAgent = (user) => !!user && AGENT_ROLES.includes(user.role);

const DEFAULT_PROMPTS = [
  'Give me the morning front desk brief',
  'Check out every guest whose folio is settled',
  'Remind in-house guests who still owe money, with a payment link',
];

export const TAB_PROMPTS = {
  bookings: [
    'Check out every guest whose folio is settled',
    'Which in-house guests still owe money?',
    "Are today's arrivals ready? Confirm anything pending",
    'Export in-house guests with a balance as a CSV',
  ],
  'night-audit': [
    'Which departures are still checked in, and why?',
    'List overdue check-outs with their balances',
    'Check out every guest whose folio is settled',
  ],
  'web-checkins': [
    "Which of today's arrivals have not completed web check-in?",
    'Email arrivals with a pending balance their payment link',
  ],
  'staff-reservation': ['Which rooms are free for the next three nights?', 'Is a female dorm bed free this weekend?'],
  services: ['Show the services catalogue with GST rates', 'Which in-house guests have unpaid services?'],
  addons: ['Show the services catalogue with GST rates'],
  items: [
    'Classify inventory added last month by service and total the GST',
    'Which items are below minimum stock?',
    'Restock white robes by 20 units',
  ],
  courses: ['Which courses start in the next 30 days?'],
  rooms: ['Block room D104 for deep cleaning tomorrow', 'Which rooms are free tonight?'],
  availability: [
    'Which rooms are free for the next three nights?',
    'Why are the female dorm beds unavailable next week?',
    'Block room D104 for maintenance tomorrow',
  ],
  pricing: ['Show room types with base prices and GST'],
  'payment-log': [
    'Total payments received this week by method',
    'Email me a CSV of this week’s payments',
    'Remind in-house guests who still owe money',
  ],
  reconciliation: ['Which in-house guests have a balance due?', 'Payments report for today'],
  'new-reports': ['Summarise occupancy for this month', 'Export last week’s accounts report as a PDF and email it to me'],
  'custom-reports': ['Group this month’s payments by method and total them'],
  users: ['What did the agent do for my team this week?'],
  audit: ['Show the agent actions executed today'],
  default: DEFAULT_PROMPTS,
};

export const promptsForTab = (tab) => TAB_PROMPTS[tab] || DEFAULT_PROMPTS;

export const TOOL_LABELS = {
  get_today_overview: 'Front desk overview',
  search_bookings: 'Searched bookings',
  get_booking_folio: 'Opened folio',
  list_checkout_candidates: 'Checked departures',
  list_arrivals: 'Checked arrivals',
  list_outstanding_balances: 'Found balances due',
  get_payment_link: 'Payment link',
  list_inventory_items: 'Read inventory',
  list_services_catalog: 'Read catalogue',
  check_availability: 'Checked availability',
  get_room_calendar: 'Checked room calendar',
  list_availability_blocks: 'Read availability blocks',
  export_data_file: 'Exported file',
  list_exported_files: 'Listed files',
  list_whatsapp_templates: 'Read WhatsApp templates',
  send_email: 'Send email',
  send_whatsapp: 'Send WhatsApp',
  get_report: 'Ran report',
  query_database: 'Queried PMS data',
  aggregate_rows: 'Calculated totals',
  confirm_bookings: 'Confirm bookings',
  check_in_bookings: 'Check in guests',
  check_out_bookings: 'Check out guests',
  update_booking_notes: 'Update booking notes',
  record_payment: 'Record payment',
  send_guest_messages: 'Message guests',
  adjust_inventory: 'Adjust stock',
  create_availability_blocks: 'Block rooms',
  get_agent_action_history: 'Read action history',
  undo_agent_actions: 'Undo actions',
  list_skills: 'Listed skills',
  load_skill: 'Loaded skill',
  save_skill: 'Save skill',
};

export function humanize(key) {
  const spaced = String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const toolLabel = (name) => TOOL_LABELS[name] || humanize(name);

export const PROVIDER_ORDER = ['anthropic', 'openai', 'gemini', 'meta', 'groq'];
